import React, { useEffect, useState } from 'react';
import api from '../../../utils/axios';
import { toast } from 'sonner';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { KeyRound, Eye, EyeOff, Loader2, CheckCircle2, XCircle, Save, PlugZap } from 'lucide-react';

const WayaCredencialesCard = () => {
  const [status, setStatus] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/kpis/automatizaciones/config/waya-credenciales');
      setStatus(r.data);
      setEmail(r.data.email || '');
    } catch (e) {
      toast.error('No se pudieron cargar las credenciales de Waya');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!email.trim()) { toast.error('Ingresá el usuario/email de Waya'); return; }
    setSaving(true);
    try {
      const r = await api.put('/kpis/automatizaciones/config/waya-credenciales', {
        email: email.trim(),
        password: password || undefined,
      });
      setStatus(r.data);
      setPassword('');
      setTestResult(null);
      toast.success('Credenciales de Waya guardadas y aplicadas a las automatizaciones.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar las credenciales');
    } finally {
      setSaving(false);
    }
  };

  const probar = async () => {
    if (!email.trim()) { toast.error('Ingresá el usuario/email de Waya'); return; }
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post('/kpis/automatizaciones/config/waya-credenciales/probar', {
        email: email.trim() || undefined,
        password: password || undefined,
      });
      setTestResult(r.data);
      if (r.data.success) toast.success('Conexión con Waya exitosa');
      else toast.error('Waya rechazó la conexión');
    } catch (e) {
      setTestResult({ success: false, message: e.response?.data?.detail || 'Error al probar la conexión' });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card className="p-5 lg:p-6 mb-8" data-testid="waya-cred-card">
      <div className="flex items-start gap-3">
        <div className="p-2.5 rounded-xl bg-slate-900 shrink-0"><KeyRound size={22} className="text-white" /></div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold text-slate-900">Credenciales de Waya</h2>
            {status && (
              <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100" data-testid="waya-cred-source">
                {status.source === 'db' ? 'Guardadas en la app' : 'Desde variables de entorno'}
              </Badge>
            )}
          </div>
          <p className="text-sm text-slate-500 mt-1">Usuario y contraseña que usan <b>todas</b> las automatizaciones de Waya para ingresar al portal. Los cambios se aplican al instante, sin reiniciar.</p>

          {loading ? (
            <div className="flex items-center py-6 text-slate-400"><Loader2 className="animate-spin mr-2" size={16} /> Cargando…</div>
          ) : (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">Usuario / Email</Label>
                <Input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@waya.com.ar"
                  className="mt-1"
                  data-testid="waya-cred-email"
                />
              </div>
              <div>
                <Label className="text-sm flex items-center gap-2">
                  Contraseña
                  {status?.password_set
                    ? <span className="text-xs text-emerald-600 font-normal inline-flex items-center gap-1"><CheckCircle2 size={12} /> configurada</span>
                    : <span className="text-xs text-rose-500 font-normal">sin configurar</span>}
                </Label>
                <div className="relative mt-1">
                  <Input
                    type={showPass ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={status?.password_set ? 'Dejá vacío para conservar la actual' : 'Ingresá la contraseña'}
                    className="pr-10"
                    data-testid="waya-cred-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    data-testid="waya-cred-toggle-pass"
                    aria-label={showPass ? 'Ocultar' : 'Mostrar'}
                  >
                    {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
            </div>
          )}

          {testResult && (
            <div
              className={`mt-4 rounded-lg border p-3 text-sm flex items-start gap-2 ${testResult.success ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-rose-50 border-rose-200 text-rose-700'}`}
              data-testid="waya-cred-test-result"
            >
              {testResult.success ? <CheckCircle2 size={16} className="mt-0.5 shrink-0" /> : <XCircle size={16} className="mt-0.5 shrink-0" />}
              <span>{testResult.message}</span>
            </div>
          )}

          {!loading && (
            <div className="flex flex-wrap gap-2 mt-4">
              <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="waya-cred-guardar">
                {saving ? <Loader2 size={15} className="animate-spin mr-1.5" /> : <Save size={15} className="mr-1.5" />} Guardar
              </Button>
              <Button variant="outline" onClick={probar} disabled={testing} data-testid="waya-cred-probar">
                {testing ? <Loader2 size={15} className="animate-spin mr-1.5" /> : <PlugZap size={15} className="mr-1.5" />}
                {testing ? 'Probando… (~15s)' : 'Probar conexión'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default WayaCredencialesCard;
